<script lang="ts">
import type { LanguageSupport } from '@codemirror/language';
import type { Extension } from '@codemirror/state';
import type { CodeLanguage } from '#layers/ui/utils/codemirror-languages';
import type { TextareaProps } from './Textarea.vue';
import { tv } from 'tailwind-variants';
import { LineWrappingMode } from '#layers/ui/constants/line-wrapping';

export interface InputCodeProps extends Pick<TextareaProps, 'placeholder' | 'size' | 'loading'> {
	variant?: 'outline' | 'subtle';
	block?: boolean;
	copy?: boolean;
	disabled?: boolean;
	readonly?: boolean;
	/** Read-only syntax-highlighted display that should not receive focus. */
	inert?: boolean;
	/** Syntax highlighting language. Loaded asynchronously. */
	codeLanguage?: CodeLanguage;
	/** Show line numbers in the gutter. */
	lineNumbers?: boolean;
	/** Enable inline diagnostics. No-op if the selected language has no linter. Loaded asynchronously. */
	lint?: boolean;
	/**
	 * Line wrapping behaviour:
	 * - `toggle` (default): wrapping is on and the toolbar shows a toggle to turn it off.
	 * - `on`: wrapping forced on, no toggle.
	 * - `off`: wrapping forced off, no toggle.
	 */
	lineWrapping?: LineWrappingMode;
	maxHeightBeforeExpand?: number;
}

// Reveal on hover/focus; shared by the toolbar and the ExpandableContent toggle.
const revealOnHover = 'opacity-0 transition-opacity group-hover/input-code:opacity-100 group-focus-within/input-code:opacity-100';

const inputCode = tv({
	slots: {
		root: `
			group/input-code relative isolate min-w-0 overflow-hidden rounded-md
			after:pointer-events-none after:absolute after:inset-0 after:z-10 after:rounded-[inherit] after:transition-shadow
			after:ring-inset
			focus-within:after:ring-2 focus-within:after:ring-primary
		`,
		wrapper: 'relative flex w-full min-w-0 flex-1 overflow-hidden rounded-[inherit]',
		editor: `
			size-full
			[&_.cm-editor]:size-full! [&_.cm-editor]:font-mono! [&_.cm-editor]:[background:transparent]!
			[&_.cm-editor_.cm-scroller]:overflow-auto!
			[&_.cm-editor.cm-focused]:[box-shadow:none]!
			[&_.cm-line]:font-mono!
		`,
		toolbar: `absolute z-20 flex items-center gap-0.5 rounded-full border border-default bg-default p-0.5 ${revealOnHover}`,
		expandToggle: revealOnHover,
	},
	variants: {
		variant: {
			outline: {
				root: 'bg-default after:ring after:ring-default',
				wrapper: '',
			},
			subtle: {
				root: 'bg-elevated',
			},
		},
		disabled: {
			true: {
				root: 'cursor-not-allowed opacity-50',
			},
			false: {},
		},
		readonly: {
			true: {
				root: 'cursor-default',
			},
			false: {
				root: 'cursor-text',
			},
		},
		block: {
			true: {
				root: 'flex size-full',
			},
			false: {
				root: 'inline-flex w-auto max-w-full self-start',
			},
		},
		size: {
			xs: {
				editor: 'text-xs [&_.cm-content]:py-2!',
				toolbar: 'inset-e-1.5',
			},
			sm: {
				editor: 'text-xs [&_.cm-content]:py-2!',
				toolbar: 'inset-e-2',
			},
			md: {
				editor: 'text-sm [&_.cm-content]:py-2.5!',
				toolbar: 'inset-e-2.5',
			},
			lg: {
				editor: 'text-base [&_.cm-content]:py-3!',
				toolbar: 'inset-e-2.5',
			},
			xl: {
				editor: 'rounded-xl text-base [&_.cm-content]:py-3.5!',
				toolbar: 'inset-e-2.5',
			},
		},
		gutter: {
			true: '',
			false: '',
		},
		// Center the toolbar on a single-row editor; top-anchor it otherwise.
		singleRow: {
			true: {
				toolbar: 'top-1/2 -translate-y-1/2',
			},
			false: {},
		},
	},
	compoundVariants: [
		{ variant: 'outline', disabled: false, class: { root: 'not-focus-within:hover:after:ring-accented' } },
		{ size: 'xs', gutter: false, class: { editor: '[&_.cm-editor]:ps-1.5! [&_.cm-editor]:pe-3!' } },
		{ size: 'sm', gutter: false, class: { editor: '[&_.cm-editor]:ps-1.5! [&_.cm-editor]:pe-3!' } },
		{ size: 'md', gutter: false, class: { editor: '[&_.cm-editor]:ps-1.5! [&_.cm-editor]:pe-3!' } },
		{ size: 'lg', gutter: false, class: { editor: '[&_.cm-editor]:ps-1.5! [&_.cm-editor]:pe-3!' } },
		{ size: 'xl', gutter: false, class: { editor: '[&_.cm-editor]:ps-2.5! [&_.cm-editor]:pe-4!' } },
		{ size: 'xs', singleRow: false, class: { toolbar: 'top-1.5' } },
		{ size: 'sm', singleRow: false, class: { toolbar: 'top-2' } },
		{ size: 'md', singleRow: false, class: { toolbar: 'top-2.5' } },
		{ size: 'lg', singleRow: false, class: { toolbar: 'top-2.5' } },
		{ size: 'xl', singleRow: false, class: { toolbar: 'top-2.5' } },
	],
});
</script>

<script setup lang="ts">
import { EditorView, lineNumbers as lineNumbersExtension } from '@codemirror/view';
import { createReusableTemplate, useResizeObserver } from '@vueuse/core';
import { computed, ref, shallowRef, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useColorMode } from '#imports';
import ExpandableContent from '#layers/ui/components/ExpandableContent.vue';
import { useCodeMirror } from '#layers/ui/composables/use-codemirror';
import { languageLoaders } from '#layers/ui/utils/codemirror-languages';
import { vsCodeDark } from '#layers/ui/utils/codemirror-themes/dark';
import { vsCodeLight } from '#layers/ui/utils/codemirror-themes/light';

const props = withDefaults(defineProps<InputCodeProps>(), {
	block: true,
	size: 'md',
	variant: 'outline',
});

const modelValue = defineModel<string>({ default: '' });

const { t } = useI18n();

const editorRef = useTemplateRef<HTMLElement>('editor');

const langExtension = shallowRef<LanguageSupport>();
const lintExtensions = shallowRef<Extension[]>([]);

watch(
	() => [props.codeLanguage, props.lint] as const,
	async ([language, lint], _, onCleanup) => {
		let cancelled = false;

		onCleanup(() => {
			cancelled = true;
		});

		if (!language || !(language in languageLoaders)) {
			langExtension.value = undefined;
			lintExtensions.value = [];
			return;
		}

		const loaded = await languageLoaders[language]();
		if (cancelled) return;
		langExtension.value = loaded.lang;

		if (lint && loaded.lintSource) {
			const { linter, lintGutter } = await import('@codemirror/lint');
			if (cancelled) return;
			lintExtensions.value = [linter(loaded.lintSource), lintGutter()];
		}
		else {
			lintExtensions.value = [];
		}
	},
	{ immediate: true },
);

const { hasLongLine, extension: lineOverflowExtension } = useLineOverflow();
const singleRow = computed(() => !modelValue.value.includes('\n') && !hasLongLine.value);

const colorMode = useColorMode();
const theme = computed(() => colorMode.value === 'dark' ? vsCodeDark : vsCodeLight);
const extensions = computed(() => {
	const result: Extension[] = [theme.value, lineOverflowExtension];
	if (langExtension.value) result.push(langExtension.value);
	if (props.lineNumbers) result.push(lineNumbersExtension());
	result.push(...lintExtensions.value);
	return result;
});

const localLineWrapping = ref(true);
const wrapMode = computed(() => props.lineWrapping ?? LineWrappingMode.Toggle);
const lineWrappingEnabled = computed(() => {
	if (wrapMode.value === LineWrappingMode.On) return true;
	if (wrapMode.value === LineWrappingMode.Off) return false;
	return localLineWrapping.value;
});

// Only offer the toggle when a line actually wraps or overflows.
const showLineWrappingToggle = computed(() =>
	wrapMode.value === LineWrappingMode.Toggle && hasLongLine.value,
);
const lineWrappingLabel = computed(() => t(localLineWrapping.value ? 'common.disable_line_wrap' : 'common.enable_line_wrap'));

function toggleLineWrapping() {
	localLineWrapping.value = !localLineWrapping.value;
}

const { focus } = useCodeMirror(editorRef, {
	modelValue,
	extensions,
	placeholder: computed(() => props.placeholder),
	disabled: computed(() => props.disabled),
	readonly: computed(() => props.readonly),
	inert: computed(() => props.inert),
	lineWrapping: lineWrappingEnabled,
});

const ui = computed(() =>
	inputCode({
		variant: props.variant,
		size: props.size,
		disabled: props.disabled,
		readonly: props.readonly || props.inert,
		block: props.block,
		gutter: props.lineNumbers || lintExtensions.value.length > 0,
		singleRow: singleRow.value,
	}),
);

const [DefineInputCode, ReuseInputCode] = createReusableTemplate<{
	expanded?: boolean;
	overflows?: boolean;
	collapse?: () => void;
}>();

// Whether any line is too long to fit one row (wrapped, or overflowing when wrap
// is off). Measured from the DOM because CodeMirror's `defaultLineHeight` doesn't
// match the themed line height.
function useLineOverflow() {
	const WRAPPED_ROW_THRESHOLD = 1.5; // >1 row tall, with slack for sub-pixel rounding
	const OVERFLOW_TOLERANCE_PX = 1;

	const hasLongLine = ref(false);

	function measure() {
		const scrollDOM = editorRef.value?.querySelector('.cm-scroller');
		const contentDOM = editorRef.value?.querySelector('.cm-content');

		// Can't measure a hidden/zero-width editor (e.g. an inactive tab panel).
		if (!scrollDOM || !contentDOM || scrollDOM.clientWidth === 0) return;

		const rowHeight = Number.parseFloat(getComputedStyle(contentDOM).lineHeight);
		const wrapsToMultipleRows = rowHeight > 0
			&& [...contentDOM.querySelectorAll('.cm-line')].some((line) => line.getBoundingClientRect().height > rowHeight * WRAPPED_ROW_THRESHOLD);
		const overflowsHorizontally = scrollDOM.scrollWidth > scrollDOM.clientWidth + OVERFLOW_TOLERANCE_PX;

		hasLongLine.value = wrapsToMultipleRows || overflowsHorizontally;
	}

	const extension = EditorView.updateListener.of((update) => {
		if (update.geometryChanged || update.docChanged || update.viewportChanged) measure();
	});
	// Catches size/visibility changes CodeMirror doesn't emit an update for (e.g. an inactive tab opening).
	useResizeObserver(editorRef, measure);

	return { hasLongLine, extension };
}
</script>

<template>
	<div :class="ui.root()" @click.self="focus">
		<DefineInputCode v-slot="{ expanded, overflows, collapse }">
			<div :class="ui.wrapper()" @click.self="focus">
				<div ref="editor" :class="ui.editor()" />

				<div v-if="props.copy || showLineWrappingToggle || (expanded && overflows)" :class="ui.toolbar()">
					<DTooltip v-if="expanded && overflows" :text="$t('common.collapse')">
						<DButton
							icon="lucide:chevron-up"
							color="neutral"
							variant="ghost"
							size="xs"
							square
							rounded
							class="text-dimmed"
							:aria-label="$t('common.collapse')"
							@click="collapse?.()"
						/>
					</DTooltip>

					<DTooltip v-if="showLineWrappingToggle" :text="lineWrappingLabel">
						<DButton
							:icon="localLineWrapping ? 'lucide:wrap-text' : 'lucide:arrow-right'"
							color="neutral"
							variant="ghost"
							size="xs"
							square
							rounded
							class="text-dimmed"
							:aria-label="lineWrappingLabel"
							data-testid="line-wrapping-toggle"
							@click="toggleLineWrapping"
						/>
					</DTooltip>

					<DCopyButton
						v-if="props.copy"
						:value="modelValue"
						color="neutral"
						variant="ghost"
						size="xs"
						square
						rounded
						:disabled
						:loading
					/>
				</div>
			</div>
		</DefineInputCode>
		<ExpandableContent
			v-if="maxHeightBeforeExpand"
			:max-height="maxHeightBeforeExpand"
			:toggle-class="ui.expandToggle()"
		>
			<template #default="{ expanded, overflows, collapse }">
				<ReuseInputCode :expanded :overflows :collapse />
			</template>
		</ExpandableContent>
		<ReuseInputCode v-else />
	</div>
</template>
